import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { formatCurrencyEUR, formatRelative } from "@/lib/utils";
import { MDF_FUND_STATUS_LABEL, type MdfFundStatus } from "@colab/shared";
import { useFund } from "./use-fund";
import { FundConfigurationTab } from "./fund-configuration-tab";
import { FundAllocationsTab } from "./fund-allocations-tab";
import { FundRequestsTab } from "./fund-requests-tab";
import { FundRoiTab } from "./fund-roi-tab";

// Phase 7 sub-bucket 15 — admin fund detail. Ports
// /app/admin/mdf/[id]/page.tsx (206 LOC) with the same four tabs
// (configuration / allocations / requests / roi). Tab key lives in
// `?tab=...` so navigation between tabs is a URL change; the tabs share
// the parent's `useFund(id)` cache so a sibling-tab edit reflects in the
// other tab on next visit without refetch.
//
// The legacy default tab was `configuration`; we keep that.

type Tab = "configuration" | "allocations" | "requests" | "roi";

const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: "configuration", label: "Configuration" },
  { key: "allocations", label: "Allocations" },
  { key: "requests", label: "Requests" },
  { key: "roi", label: "ROI" },
];

function normalizeTab(raw: string | null): Tab {
  if (
    raw === "configuration" ||
    raw === "allocations" ||
    raw === "requests" ||
    raw === "roi"
  )
    return raw;
  return "configuration";
}

const FUND_STATUS_VARIANT: Record<
  MdfFundStatus,
  "default" | "outline" | "secondary"
> = {
  open: "default",
  paused: "outline",
  closed: "secondary",
};

export function MdfFundDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeTab(searchParams.get("tab"));

  if (!id) return <Navigate to="/admin/mdf" replace />;

  return (
    <FundDetailInner
      fundId={id}
      tab={tab}
      onTabChange={(next) => {
        const sp = new URLSearchParams(searchParams);
        sp.set("tab", next);
        setSearchParams(sp, { replace: true });
      }}
    />
  );
}

function FundDetailInner({
  fundId,
  tab,
  onTabChange,
}: {
  fundId: string;
  tab: Tab;
  onTabChange: (next: Tab) => void;
}) {
  const query = useFund(fundId);

  if (query.isLoading) return <FundDetailSkeleton />;
  if (query.isError || !query.data) {
    return (
      <div>
        <BackLink />
        <EmptyState
          icon={ArrowLeft}
          title="Couldn't load fund"
          description={query.error?.message ?? "The fund may have been deleted."}
        />
      </div>
    );
  }

  const { fund, allocations, request_count } = query.data;
  const allocatedTotal = allocations.reduce(
    (sum, a) => sum + a.cap_amount_eur,
    0,
  );
  const spentTotal = allocations.reduce(
    (sum, a) => sum + a.used_amount_eur,
    0,
  );
  const remainingTotal = Math.max(fund.total_amount_eur - allocatedTotal, 0);
  const hasAllocations = allocations.length > 0;

  return (
    <div>
      <BackLink />

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-semibold tracking-tight">
              {fund.name}
            </h1>
            <Badge variant={FUND_STATUS_VARIANT[fund.status]}>
              {MDF_FUND_STATUS_LABEL[fund.status]}
            </Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            /{fund.slug} · Last updated {formatRelative(fund.updated_at)}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6 text-right tabular-nums">
          <Metric label="Allocated" value={formatCurrencyEUR(allocatedTotal)} />
          <Metric label="Spent" value={formatCurrencyEUR(spentTotal)} />
          <Metric label="Remaining" value={formatCurrencyEUR(remainingTotal)} />
        </div>
      </div>

      <TabsNav
        active={tab}
        onChange={onTabChange}
        allocationsCount={allocations.length}
        requestsCount={request_count}
      />

      <div className="mt-5">
        {tab === "configuration" && (
          <FundConfigurationTab fund={fund} hasAllocations={hasAllocations} />
        )}
        {tab === "allocations" && (
          <FundAllocationsTab
            fundId={fundId}
            allocations={allocations}
            fundStatus={fund.status}
          />
        )}
        {tab === "requests" && <FundRequestsTab fundId={fundId} />}
        {tab === "roi" && <FundRoiTab fundId={fundId} />}
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/admin/mdf"
      className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3 w-3" />
      Back to funds
    </Link>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 font-display text-base font-semibold">{value}</p>
    </div>
  );
}

function TabsNav({
  active,
  onChange,
  allocationsCount,
  requestsCount,
}: {
  active: Tab;
  onChange: (next: Tab) => void;
  allocationsCount: number;
  requestsCount: number;
}) {
  return (
    <div className="flex items-center gap-0.5 border-b border-border/70">
      {TABS.map((t) => {
        const count =
          t.key === "allocations"
            ? allocationsCount
            : t.key === "requests"
              ? requestsCount
              : null;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors ${
              active === t.key
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <span>{t.label}</span>
            {count !== null && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function FundDetailSkeleton() {
  return (
    <div>
      <Skeleton className="mb-4 h-3 w-24" />
      <Skeleton className="h-7 w-1/3" />
      <Skeleton className="mt-2 h-3 w-1/4" />
      <div className="mt-6 h-px w-full bg-border" />
      <Skeleton className="mt-6 h-40 w-full" />
    </div>
  );
}
